import { Link } from '@commonsku/styles';
import React, { useRef, useState } from 'react';
import { oauth } from '../../utils';
import {
  buildPoDCredentialStatusEndpoint,
  usePoDCredentialStatus,
} from '../../hooks/usePoDCredentialStatus';
import IntegrationCredentialsForm from './IntegrationCredentialsForm';

type BannerState = 'info' | 'success' | 'error';

const PrintOnDemandIntegration = () => {
  const {
    credentialStatus,
    isLoading,
    reload,
  } = usePoDCredentialStatus(true);
  const [accountIdOverride, setAccountIdOverride] = useState<string | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [bannerStateOverride, setBannerStateOverride] = useState<BannerState | null>(null);
  const lastSuccessfulAccountId = useRef<string | null>(null);
  const accountId = accountIdOverride
    ?? (isLoading ? '' : (credentialStatus.account_id ?? ''));
  const bannerState = bannerStateOverride
    ?? (credentialStatus.configured ? 'success' : 'info');

  const testConnection = async () => {
    const trimmedAccountId = accountId.trim();
    const trimmedApiKey = apiKey.trim();

    setIsTestingConnection(true);
    setBannerStateOverride('info');

    try {
      const payload: Record<string, string> = {
        account_id: trimmedAccountId,
        api_key: trimmedApiKey,
      };
      if (credentialStatus.division_id) {
        payload.division_id = credentialStatus.division_id;
      }

      await oauth('PUT', buildPoDCredentialStatusEndpoint(), payload);

      await reload();

      lastSuccessfulAccountId.current = trimmedAccountId;
      setApiKey('');
      setBannerStateOverride('success');
    } catch (_error: any) {
      setBannerStateOverride('error');
    } finally {
      setIsTestingConnection(false);
    }
  };

  /*
  const disconnectCredential = async () => {
    if (!window.confirm('Are you sure you want to disconnect Fulfill Engine credentials?')) {
      return;
    }

    setIsDisconnecting(true);
    setMessage(null);

    try {
      const endpoint = buildPoDCredentialStatusEndpoint();
      await oauth('DELETE', endpoint, {});

      setApiKey('');
      await reload();

      setMessage({
        type: 'success',
        text: 'Fulfill Engine credentials disconnected.',
      });
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error?.json?.error || 'Unable to disconnect Fulfill Engine credentials.',
      });
    } finally {
      setIsDisconnecting(false);
    }
  };
  */

  const trimmedAccountId = accountId.trim();
  const trimmedApiKey = apiKey.trim();
  const hasRequiredInputs = Boolean(trimmedAccountId) && Boolean(trimmedApiKey);
  const hasUnchangedSuccessfulAccountId = lastSuccessfulAccountId.current === trimmedAccountId;
  const isTestConnectionDisabled = isTestingConnection
    || !hasRequiredInputs
    || ('success' === bannerState && hasUnchangedSuccessfulAccountId);

  const onAccountIdChange = (value: string) => {
    setAccountIdOverride(value);
    setBannerStateOverride('info');
  };

  const onApiKeyChange = (value: string) => {
    setApiKey(value);
    setBannerStateOverride('info');
  };

  const getAlertContent = () => {
    if ('success' === bannerState) {
      return {
        alertType: 'success' as const,
        message: 'Connection successful!',
      };
    }

    if ('error' === bannerState) {
      return {
        alertType: 'error' as const,
        message: (
          <>
            We couldn&apos;t verify your credentials. Check your&nbsp;
            <Link as='a' href='https://www.fulfillengine.com/' target='_blank' rel='noreferrer noopener'>
              Fulfill Engine
            </Link>&nbsp;
            Account ID and API Key and try again.
          </>
        ),
      };
    }

    return {
      alertType: 'neutral' as const,
      message: (
        <>
          Find your Account ID and API Key in your&nbsp;
          <Link as='a' href='https://www.fulfillengine.com/' target='_blank' rel='noreferrer noopener'>
            Fulfill Engine
          </Link>&nbsp;
          account.
        </>
      ),
    };
  };

  const alertContent = getAlertContent();

  return (
    <IntegrationCredentialsForm
      isLoading={isLoading}
      alertType={alertContent.alertType}
      alertMessage={alertContent.message}
      fields={[
        {
          label: 'Account ID',
          value: accountId,
          disabled: isTestingConnection,
          onChange: onAccountIdChange,
        },
        {
          label: 'API Key',
          type: 'password',
          value: apiKey,
          placeholder: credentialStatus.configured ? '****************' : '',
          disabled: isTestingConnection,
          onChange: onApiKeyChange,
        },
      ]}
      submitLabel='Test Connection'
      isSubmitting={isTestingConnection}
      isSubmitDisabled={isTestConnectionDisabled}
      onSubmit={testConnection}
      submittingSpinnerStyle={{ margin: 'auto 28px' }}
    />
  );
};

export default PrintOnDemandIntegration;
